
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, PlusCircle, RefreshCw, Info } from 'lucide-react';

type UserProfile = {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  credits: number;
  created_at: string;
  subscription_status: string | null;
};

const UsersTab = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(10);
  const [creditDescription, setCreditDescription] = useState<string>('Admin credit grant');
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchUsers();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('profiles_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'profiles' 
        }, 
        () => {
          fetchUsers();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      console.log('Fetching all profiles as admin...');
      
      if (!isAdmin) {
        console.warn('Non-admin user attempting to access all profiles');
        toast({
          title: 'Zugriff verweigert',
          description: 'Sie haben keine Berechtigung, alle Benutzerprofile einzusehen.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }
      
      // Make sure we're using the correct table name and not applying any filters
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Log the raw response to see what we're getting
      console.log('Raw profiles data:', profiles);
      
      if (!profiles || profiles.length === 0) {
        console.warn('No profiles found in the database');
        setUsers([]);
        return;
      }
      
      // Process the profiles data into our UserProfile format
      const processedUsers: UserProfile[] = profiles.map(profile => ({
        id: profile.id,
        email: profile.email || 'No email',
        created_at: profile.created_at || new Date().toISOString(),
        credits: profile.credits || 0,
        full_name: profile.full_name || null,
        company_name: profile.company_name || null,
        subscription_status: profile.subscription_status || null
      }));
      
      console.log('Processed users:', processedUsers);
      setUsers(processedUsers);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Benutzer.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addCreditsToUser = async (userId: string) => {
    if (!userId || !creditAmount) return;
    
    setIsAddingCredits(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('admin-add-credits', {
        method: 'POST',
        body: { 
          userId,
          amount: creditAmount,
          description: creditDescription || 'Admin credit addition'
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Credits hinzugefügt',
        description: `${creditAmount} Credits wurden dem Benutzer hinzugefügt. Neuer Stand: ${data.currentCredits}`,
      });
      
      fetchUsers();
      setSelectedUserId(null);
      setCreditAmount(10);
      setCreditDescription('Admin credit grant');
      
    } catch (error: any) {
      console.error('Error adding credits:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Fehler beim Hinzufügen von Credits.',
        variant: 'destructive',
      });
    } finally {
      setIsAddingCredits(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubscriptionBadge = (status: string | null) => {
    if (!status) return <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/30">Keine</Badge>;
    
    switch (status) {
      case 'active':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">Aktiv</Badge>;
      case 'canceled':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">Gekündigt</Badge>;
      case 'free':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">Kostenlos</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.company_name?.toLowerCase().includes(searchLower)
    );
  });

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Benutzer</CardTitle>
          <CardDescription>
            Verwalten Sie registrierte Benutzer und deren Credits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Sie benötigen Administratorrechte, um alle Benutzer zu sehen.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Benutzer</CardTitle>
        <CardDescription>
          Verwalten Sie registrierte Benutzer und deren Credits.
        </CardDescription>
        <div className="mt-4 flex justify-between items-center">
          <Input
            placeholder="Suche nach Benutzern..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-1/3"
          />
          <Button 
            variant="outline"
            onClick={fetchUsers}
            className="ml-2"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-jade" />
            <p>Benutzer werden geladen...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Alert>
            <AlertDescription>
              {searchTerm ? 'Keine Benutzer gefunden.' : 'Derzeit sind keine Benutzer vorhanden.'}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Unternehmen</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Abo-Status</TableHead>
                  <TableHead>Registriert am</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.full_name || <em className="text-gray-500">Nicht angegeben</em>}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.company_name || <em className="text-gray-500">Nicht angegeben</em>}</TableCell>
                    <TableCell>{user.credits}</TableCell>
                    <TableCell>{getSubscriptionBadge(user.subscription_status)}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedUserId(selectedUserId === user.id ? null : user.id)}
                        className={selectedUserId === user.id ? "bg-jade-light text-white" : ""}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        {selectedUserId === user.id ? "Abbrechen" : "Credits hinzufügen"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {selectedUserId && (
              <div className="mt-6 p-4 border rounded-md bg-background">
                <h3 className="text-lg font-medium mb-4">Credits hinzufügen</h3>
                <div className="flex flex-col space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Anzahl Credits</label>
                    <Input
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                      className="w-full md:w-1/3"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Beschreibung</label>
                    <Input
                      value={creditDescription}
                      onChange={(e) => setCreditDescription(e.target.value)}
                      className="w-full md:w-2/3"
                      placeholder="Grund für die Gutschrift"
                    />
                  </div>
                  <div>
                    <Button 
                      onClick={() => addCreditsToUser(selectedUserId)}
                      disabled={isAddingCredits || creditAmount <= 0}
                      className="bg-jade hover:bg-jade-light"
                    >
                      {isAddingCredits ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : <PlusCircle className="h-4 w-4 mr-2" />}
                      Credits hinzufügen
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default UsersTab;
